import { Device, Group, Rule } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { GeotabCall } from "./fetchExceptionEvents";
import { readZoneLogRules } from "./syncZoneLogRules";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { FAIRFLEET_SERVICES } from "@/constants/groupConfig";
import { MAX_DEVICE_PAGES, RESULTS_LIMIT_DEVICE, RESULTS_LIMIT_GROUP } from "@/constants/resultsLimits";
import { ZONELOG_GROUP_NAME } from "@/pages/zone-report/utils/zoneLogRules";
import { fetchAllSortedById } from "@/utilities/query/fetchAllSortedById";

export interface ZoneLogSetup {
  servicesGroup?: Group;
  zonelogsGroup?: Group;
  rules: Rule[];
  /** Vehicles in „Zonelogs“ (archived included): the only ones whose stays are recorded. */
  devices: Device[];
}

const parentId = (group: Group) => (group as Group & { parent?: { id?: string } }).parent?.id;

/** „Fairfleet Services“, its subgroup „Zonelogs“, the zonelog rules and the vehicles in „Zonelogs“. */
export function useZoneLogSetup() {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["zoneLogSetup", activeSessionId],
    queryFn: async ({ signal }): Promise<ZoneLogSetup> => {
      const call: GeotabCall = (method, params, s) => geotabClient.call(method, params, s);
      const getGroups = (name: string) =>
        geotabClient.call<Group[]>("Get", { typeName: "Group", search: { name }, resultsLimit: RESULTS_LIMIT_GROUP }, signal);

      const [services, zonelogs, rules] = await Promise.all([
        getGroups(FAIRFLEET_SERVICES),
        getGroups(ZONELOG_GROUP_NAME),
        readZoneLogRules(call, signal),
      ]);
      const servicesGroup = services[0];
      // A customer may own an unrelated group of the same name; ours hangs under Fairfleet Services.
      const zonelogsGroup =
        zonelogs.find(group => servicesGroup && parentId(group) === servicesGroup.id) ??
        zonelogs.find(group => parentId(group) === undefined && servicesGroup?.children?.some(child => child.id === group.id));

      const devices = zonelogsGroup
        ? await fetchAllSortedById<"Device", Device>(
            geotabClient,
            "Device",
            // Archived vehicles too: a past period must keep their stays; the page narrows by period.
            { groups: [{ id: zonelogsGroup.id }] } as never,
            {
              pageSize: RESULTS_LIMIT_DEVICE,
              maxPages: MAX_DEVICE_PAGES,
              propertySelector: { fields: ["id", "name", "activeTo"], isIncluded: true },
              signal,
            },
          )
        : [];

      return { servicesGroup, zonelogsGroup, rules, devices };
    },
  });
}
